import React, { useRef, useEffect, useState, useContext, useMemo } from 'react';
import { LandingPageContext } from '../../contexts/LandingPageContext';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import './ArtSection.css';

gsap.registerPlugin(ScrollTrigger);

const ArtSection = ({ images = [], interval = 10000 }) => {
    const { artSection, getScrollSmoother } = useContext(LandingPageContext);
    const sectionRef = useRef(null);
    const textRef = useRef(null);

    const [displayedText, setDisplayedText] = useState('');
    const [showCursor, setShowCursor] = useState(false);

    const fullText =
        typeof artSection.fullText === 'object'
            ? artSection.fullText[i18n.language] || artSection.fullText.en || ''
            : artSection.fullText;

    const [currentIndex, setCurrentIndex] = useState(0);
    const slideRefs = useRef([]);
    const { t } = useTranslation();

    // Use images prop if provided, otherwise use images from artSection in context
    const imagesList = useMemo(() => {
        if (Array.isArray(images) && images.length) return images;
        if (Array.isArray(artSection?.images)) return artSection.images;
        return [];
    }, [images, artSection?.images]);

    const resolveSrc = (src) => {
        if (!src) return src;
        if (src.startsWith('http') || src.startsWith('//')) return src;
        // Ensure public/ served assets are referenced correctly, especially when app is deployed at root
        return `${process.env.PUBLIC_URL || ''}${src}`;
    };

    // Initial visibility
    useEffect(() => {
        imagesList.forEach((_, i) => {
            const el = slideRefs.current[i];
            if (!el) return;

            gsap.set(el, {
                opacity: i === currentIndex ? 1 : 0,
                scale: 1,
                clipPath: i === currentIndex
                    ? "inset(0 0 0% 0)"
                    : "inset(0 0 100% 0)",
                zIndex: i === currentIndex ? 2 : 1
            });
        });
    }, [imagesList, currentIndex]);

    const animateTo = (from, to) => {
        const outgoing = slideRefs.current[from];
        const incoming = slideRefs.current[to];
        if (!outgoing || !incoming) return;

        gsap.set(incoming, {
            opacity: 0,
            scale: 1.15,
            clipPath: "inset(0 0 100% 0)"
        });

        gsap.to(outgoing, {
            opacity: 0,
            scale: 1.05,
            duration: 1.2,
            ease: "power2.out"
        });

        gsap.to(incoming, {
            opacity: 1,
            scale: 1,
            clipPath: "inset(0 0 0% 0)",
            duration: 1.6,
            ease: "power3.out"
        });
    };

    // Auto-play
    useEffect(() => {
        if (imagesList.length <= 1) return;

        const id = setInterval(() => {
            setCurrentIndex(prev => {
                const next = (prev + 1) % imagesList.length;
                animateTo(prev, next);
                return next;
            });
        }, interval);

        return () => clearInterval(id);
    }, [imagesList, interval]);

    const prev = () => {
        setCurrentIndex(prev => {
            const next = (prev - 1 + imagesList.length) % imagesList.length;
            animateTo(prev, next);
            return next;
        });
    };

    const next = () => {
        setCurrentIndex(prev => {
            const next = (prev + 1) % imagesList.length;
            animateTo(prev, next);
            return next;
        });
    };

    const goTo = (index) => {
        if (index === currentIndex) return;
        const clamped = Math.max(0, Math.min(index, imagesList.length - 1));
        animateTo(currentIndex, clamped);
        setCurrentIndex(clamped);
    };


    useEffect(() => {
        const smoother = getScrollSmoother();
        if (!smoother || !sectionRef.current) return;

        ScrollTrigger.create({
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            scroller: smoother.scroller,
            onUpdate: self => {
                const progress = Math.min(self.progress / 0.6, 1);
                const chars = Math.floor(progress * fullText.length);
                setDisplayedText(fullText.slice(0, chars));
                setShowCursor(chars < fullText.length);
            }
        });

        return () => ScrollTrigger.getAll().forEach(st => st.kill());
    }, [fullText, getScrollSmoother]);

    useEffect(() => {
        const resizeText = () => {
            if (!textRef.current) return;
            const containerHeight = textRef.current.clientHeight;
            const textEl = textRef.current.querySelector('p');
            if (!textEl) return;



            const isMobile = window.innerWidth <= 900;
            if (isMobile) {

                const mobileFontSize = 20;
                textEl.style.fontSize = `${mobileFontSize}px`;
                return;
            }
            let fontSize = 10;
            textEl.style.fontSize = `${fontSize}px`;

            while (textEl.scrollHeight < containerHeight && fontSize < 1000) {
                fontSize += 1;
                textEl.style.fontSize = `${fontSize}px`;
            }

            while (textEl.scrollHeight > containerHeight && fontSize > 1) {
                fontSize -= 1;
                textEl.style.fontSize = `${fontSize}px`;
            }

            const scaleFactor = 0.9;
            textEl.style.fontSize = `${fontSize * scaleFactor}px`;
        };

        resizeText();
        window.addEventListener('resize', resizeText);
        return () => window.removeEventListener('resize', resizeText);
    }, [displayedText]);

    // Ensure current is valid whenever images change
    useEffect(() => {
        if (imagesList.length === 0) {
            setCurrentIndex(0);
            return;
        }
        setCurrentIndex(prev => Math.max(0, Math.min(prev, imagesList.length - 1)));
    }, [imagesList.length]);




    const textColor = artSection.textColor || '#000000';
    const remainingColor = artSection.remainingTextColor || '#000000';

    // Determine title text and color
    const titleColor = artSection.sectionTitleColor || '#A08B7A';
    // Use translation hook (t) so component re-renders when language changes
    const titleText = (t && typeof t === 'function')
        ? (t('nav.einzigartig') || t('sections.unique'))
        : (i18n && typeof i18n.t === 'function' ? (i18n.t('nav.einzigartig') || i18n.t('sections.unique')) : 'EINZIGARTIG');


    return (
        <section id="artSection" className="art-section" ref={sectionRef}>
            {/* Section title */}
            <div className="section-title" style={{ color: titleColor }}>
                {titleText}
            </div>

            <div className="art-text" ref={textRef}>
                <p style={{ color: textColor }}>
                    <span>{displayedText}</span>
                    <span className="remaining" style={{ color: remainingColor }}>{fullText.slice(displayedText.length)}</span>
                    {showCursor && <span className="cursor">|</span>}
                </p>
            </div>

            <div className="art-image">
                {imagesList.map((src, i) => (
                    <div
                        key={i}
                        ref={el => (slideRefs.current[i] = el)}
                        className="carousel-slide"
                        style={{ zIndex: i === currentIndex ? 2 : 1, opacity: i === currentIndex ? 1 : 0 }}
                    >
                        <img src={resolveSrc(src)} alt={`art-${i}`} draggable={false} />
                    </div>
                ))}

                {imagesList.length > 1 && (
                    <>
                        <button className="carousel-btn prev" onClick={prev} aria-label="Previous slide">‹</button>
                        <button className="carousel-btn next" onClick={next} aria-label="Next slide">›</button>

                        {/* dots navigation */}
                        <div className="carousel-dots" role="tablist" aria-label="Slide navigation">
                            {imagesList.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`carousel-dot ${i === currentIndex ? 'active' : ''}`}
                                    onClick={() => goTo(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                    aria-pressed={i === currentIndex}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default ArtSection;
